import { useQuery } from '@tanstack/react-query'
import {
  deviceLiveFrameQuery,
  useDeviceLive,
  type LiveApplication,
  type LiveCpuThread,
  type LivePerformance,
  type LiveProcess,
  type LiveProcesses,
} from '../api/deviceLive'
import { convertBytes } from './convertBytes'
import { groupConnections, groupProcesses, ipInCidr, prettySize } from './liveData'
import {
  CpuGauge,
  DISK_RANGES,
  IN_RANGES,
  LedBar,
  OUT_RANGES,
  RAM_RANGES,
  SWAP_RANGES,
} from './liveGauges'
import { tcpPortName } from './tcpPorts'
import { Spinner } from '../ui/Spinner'
import './DeviceLivePanel.css'

/**
 * "Computer performance" - cele sase cadrane care se uita la o masina VIE.
 *
 * Perechea veche: `Easeedesk::live($deviceId)` cu `easeedesk/device/live.php` si
 * cele sase machete din `easeedesk/device_live/`, desenate de
 * `Widgets.liveHealth` (`public/js/easeedesk/easeedesk.js:6500`).
 *
 * SINGURUL ecran din cele 24 ale fisei care nu se citeste dintr-o baza. Cifrele
 * vin de pe masina, prin socket, la trei secunde - vezi `src/api/portalSocket.ts`
 * pentru cale si `src/api/deviceLive.ts` pentru carlig. Din baza vine numai
 * rama (cat RAM are masina, cate fire are procesorul), fara de care panoul n-ar
 * sti nici cate randuri sa deseneze, nici fata de ce sunt procentele.
 *
 * CAND MASINA TACE, PANOUL SPUNE CA TACE.
 * ---------------------------------------------------------------------------
 * Serverul de socket nu raspunde nimic pentru o masina stinsa: nici eroare,
 * nici obiect gol - tacere. Portalul lasa in cazul asta ceasuri pe zero si
 * tabele goale, care arata exact ca o masina linistita. Aici se intreaba
 * separat, cu `CHECK_DEVICE` (singurul eveniment cu raspuns), si se scrie ce e:
 * masina nu e conectata, sau legatura cu serverul nu merge. Un ecran care nu
 * stie e mai bun decat unul care arata zero si pare ca stie.
 */
export function DeviceLivePanel({ id }: { id: number }) {
  const { data: frame, error } = useQuery(deviceLiveFrameQuery(id))
  const live = useDeviceLive(id, ['performance', 'processes'])

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!frame) {
    return <Spinner />
  }

  const perf = (live.data.performance ?? {}) as LivePerformance
  const fresh = live.at > 0

  /* Incarcarea procesorului: media firelor daca masina le trimite, altfel
     `cpuLoad` ca atare. Exact ce facea portalul (`easeedesk.js:6913-6929`) -
     pe unele masini `cpuLoad` vine zero desi firele arata munca. */
  const threads: LiveCpuThread[] = Array.isArray(perf.cpuUsage) ? perf.cpuUsage : []
  const numeric = threads.filter((t) => Number.isFinite(Number(t.Name)) && Number.isFinite(Number(t.Usage)))
  const cpuLoad = numeric.length
    ? Math.round(numeric.reduce((sum, t) => sum + Number(t.Usage), 0) / numeric.length)
    : Number(perf.cpuLoad ?? (fresh ? 0 : frame.cpu.usage))

  const usageByThread = new Map<string, number>()
  for (const thread of threads) {
    usageByThread.set(String(thread.Name), Number(thread.Usage))
  }

  /* Procesele vin pe doua cai: in `performance.processes.app` si prin
     evenimentul `processes`. Portalul le desena pe amandoua in acelasi tabel,
     cine ajungea ultimul. Aici castiga evenimentul dedicat, cand exista. */
  const fromEvent = (live.data.processes as LiveProcesses | undefined)?.app
  const rawProcesses: LiveProcess[] = Array.isArray(fromEvent)
    ? fromEvent
    : Array.isArray(perf.processes?.app)
      ? perf.processes.app
      : []
  const processes = groupProcesses(rawProcesses)

  const apps: LiveApplication[] = Array.isArray(perf.applications) ? perf.applications : []
  const foreground = perf.foreground
  const connections = groupConnections(
    Array.isArray(perf.connectionsEsdtablished) ? perf.connectionsEsdtablished : []
  )

  const bandwidth = Number(perf.bandwidth ?? 0)
  const inPercent = bandwidth > 0 ? (Number(perf.bytesReceivedperSec ?? 0) * 100) / bandwidth * 100 : 0
  const outPercent = bandwidth > 0 ? (Number(perf.bytesSentperSec ?? 0) * 100) / bandwidth * 100 : 0

  const usedRam = usedOf(perf.totalMemory, perf.memoryFree, frame.memory.ram, frame.memory.ram_free)
  /* Swapul vine de pe masina in MEGAOCTETI, spre deosebire de memorie. Asa il
     inmultea si portalul (`easeedesk.js:7030`). Rama, in schimb, e deja in
     octeti. */
  const usedSwap =
    perf.totalSwap !== undefined && perf.swapFree !== undefined
      ? (Number(perf.totalSwap) - Number(perf.swapFree)) * 1024 * 1024
      : usedOf(undefined, undefined, frame.memory.swap, frame.memory.swap_free)

  return (
    <div className="dl-panel">
      <div className="row">
        <div className="col-md-12">
          <h5 className="bordered">Computer performance</h5>
        </div>
      </div>

      <LiveState status={live.status} online={live.online} fresh={fresh} />

      <div className="row is-flex">
        <div className="col-md-4">
          <div className="dl-box">
            <div className="dl-cpu-model">{frame.cpu.model}</div>
            <CpuGauge value={cpuLoad} />
            <table className="dl-threads">
              <tbody>
                {Array.from({ length: frame.cpu.threads }, (_, i) => {
                  const usage = usageByThread.get(String(i)) ?? 0
                  return (
                    <tr key={i}>
                      <td>CPU{i}</td>
                      <td className="dl-threads-bar">
                        <div className="progress">
                          <div
                            className="progress-bar progress-bar-primary"
                            role="progressbar"
                            style={{ width: `${Math.max(0, Math.min(100, usage))}%` }}
                          />
                        </div>
                      </td>
                      <td>
                        <span className="color-blue">{Math.round(usage)}%</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="dl-box">
            <h5>MEMORY</h5>
            <table className="dl-memory">
              <tbody>
                <tr>
                  <td className="align-center">
                    RAM
                    <br />
                    <span className="dl-dim">{size(frame.memory.ram)}</span>
                  </td>
                  <td className="align-center">
                    SWAP
                    <br />
                    <span className="dl-dim">{size(frame.memory.swap)}</span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <LedBar value={Number(perf.memoryLoad ?? 0)} ranges={RAM_RANGES} vertical />
                  </td>
                  <td>
                    <LedBar value={Number(perf.swapLoad ?? 0)} ranges={SWAP_RANGES} vertical />
                  </td>
                </tr>
                <tr>
                  <td>
                    Used RAM
                    <br />
                    <span className="dl-dim">{size(usedRam)}</span>
                  </td>
                  <td>
                    Used SWAP
                    <br />
                    <span className="dl-dim">{size(usedSwap)}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="dl-box">
            <h5>
              {frame.storage.type} {frame.storage.size === null ? 'n/a' : convertBytes(frame.storage.size)}
            </h5>
            <table className="dl-memory">
              <tbody>
                <tr>
                  <td className="align-center">READ</td>
                  <td className="align-center">WRITE</td>
                </tr>
                <tr>
                  <td>
                    <LedBar value={Number(perf.discReadsPerSecond ?? 0)} ranges={DISK_RANGES} vertical />
                    <span className="dl-dim">{`${prettySize(Number(perf.diskReadBytesPerSec ?? 0))}/s`}</span>
                  </td>
                  <td>
                    <LedBar value={Number(perf.discWritesPerSecond ?? 0)} ranges={DISK_RANGES} vertical />
                    <span className="dl-dim">{`${prettySize(Number(perf.diskWriteBytesPerSec ?? 0))}/s`}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="dl-box">
            <h5>Active Processes</h5>
            <p className="dl-count">{processes.length}</p>
            <table className="table-list">
              <thead>
                <tr>
                  <th className="align-left">PROCESS NAME</th>
                  <th className="align-right">CPU LOAD</th>
                  <th className="align-right">MEMORY</th>
                </tr>
              </thead>
              <tbody>
                {processes.map((process) => (
                  <tr key={process.name}>
                    <td>
                      <i className="fa fa-window-maximize"></i> {process.name}
                    </td>
                    <td>{process.cpuLoad}%</td>
                    <td>{prettySize(process.memory)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="dl-box">
            <h5>Active Windows</h5>
            <p className="dl-count">{apps.length}</p>
            <table className="table-list">
              <thead>
                <tr>
                  <th className="align-left">APPLICATION NAME</th>
                </tr>
              </thead>
              <tbody>
                {foreground && foreground.Title !== undefined && foreground.Title !== '' ? (
                  <tr>
                    <td>
                      <i className="fa fa-window-maximize"></i> <strong>{foreground.Title}</strong>
                    </td>
                  </tr>
                ) : null}
                {apps
                  .filter((app) => foreground?.PID !== app.PID)
                  .map((app, i) => (
                    <tr key={`${app.PID ?? 'x'}-${i}`}>
                      <td>
                        <i className="fa fa-window-maximize"></i> {app.Title}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="dl-box">
            <h5>Established Connections</h5>
            <p className="dl-count">{connections.length}</p>
            <div className="dl-network">
              <LedBar value={inPercent} ranges={IN_RANGES} label={`${prettySize(Number(perf.bytesReceivedperSec ?? 0))}/s in`} />
              <LedBar value={outPercent} ranges={OUT_RANGES} label={`${prettySize(Number(perf.bytesSentperSec ?? 0))}/s out`} />
            </div>
            <table className="table-list">
              <thead>
                <tr>
                  <th className="align-left">IP</th>
                  <th className="align-right">TCP</th>
                  <th className="align-right">PROTOCOL</th>
                  <th className="align-right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {connections
                  /* Conexiunile catre OptimumDesk nu se arata: sunt ale
                     agentului, si ar fi in fiecare lista, pe fiecare masina.
                     Acelasi interval ca in portal (`easeedesk.js:6996`). */
                  .filter((con) => !ipInCidr(con.address.split(':')[0] ?? '', '65.49.35.0/24'))
                  .map((con) => {
                    const [ip, port] = con.address.split(':')
                    const name = tcpPortName(port ?? '')
                    return (
                      <tr key={con.address}>
                        <td>{ip}</td>
                        <td>{name ?? <span className="color-danger">{port}</span>}</td>
                        <td>{con.protocol}</td>
                        <td className="dl-total">{con.total_connections}</td>
                      </tr>
                    )
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * De ce nu se vede nimic - cand chiar nu se vede nimic.
 *
 * Cat timp curg date nu se scrie nimic: o dunga permanenta "totul e bine" e
 * zgomot. Se scrie doar cand ceva NU merge, si atunci se spune ce anume.
 */
function LiveState({
  status,
  online,
  fresh,
}: {
  status: string
  online: boolean | null
  fresh: boolean
}) {
  if (fresh) {
    return null
  }

  if (status === 'unconfigured') {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> Live data is not configured on this
        installation: there is no socket server address. The numbers below are the last ones known from
        the database, not what the machine is doing now.
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Cannot reach the realtime
        server. Retrying.
      </div>
    )
  }

  if (online === false) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-plug" aria-hidden="true"></i> This machine is not connected right now, so it
        has nothing to report. What you see is the last state known from the database.
      </div>
    )
  }

  return (
    <div className="alert alert-info" role="alert">
      <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i> Asking the machine...
    </div>
  )
}

/** Ce s-a folosit, din ce raporteaza masina; daca inca n-a raportat, din baza. */
function usedOf(
  total: number | undefined,
  free: number | undefined,
  frameTotal: number | null,
  frameFree: number | null
): number | null {
  if (total !== undefined && free !== undefined) {
    return Number(total) - Number(free)
  }
  if (frameTotal !== null && frameFree !== null) {
    return frameTotal - frameFree
  }

  return null
}

function size(bytes: number | null): string {
  return bytes === null || bytes <= 0 ? 'n/a' : convertBytes(bytes)
}
